/**
 * Header repo-picker. Renders the active repo as `owner/name` and lets
 * the operator switch — selection persists via `<ReposProvider>` and
 * triggers a `useRevalidator()` so every loader on screen refetches.
 *
 * Designed for the sticky app header (compact, single-line). When the
 * operator has zero or one repo, the trigger collapses to a static
 * label rather than offering a useless dropdown.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  cn,
} from '@repo-manager/ui';

import { useRepos } from '../lib/repo-context';

export function RepoSelect({ className }: { className?: string }) {
  const { repos, loading, activeRepoId, activeRepo, setActiveRepoId } = useRepos();

  if (loading && repos.length === 0) {
    return <Skeleton className={cn('h-8 w-44', className)} />;
  }

  if (repos.length === 0) {
    return (
      <div
        className={cn(
          'inline-flex h-8 items-center rounded-md border border-dashed border-border px-2 text-xs text-muted-foreground',
          className,
        )}
      >
        no repos onboarded
      </div>
    );
  }

  if (repos.length === 1) {
    const r = repos[0]!;
    return (
      <div
        className={cn(
          'inline-flex h-8 items-center gap-2 rounded-md border border-border bg-muted/40 px-2 font-mono text-xs',
          className,
        )}
        aria-label="Active repo"
      >
        <span className="text-muted-foreground">repo</span>
        <span>
          {r.owner}/{r.name}
        </span>
      </div>
    );
  }

  return (
    <Select
      value={activeRepoId ?? undefined}
      onValueChange={(id) => setActiveRepoId(id)}
    >
      <SelectTrigger
        className={cn('h-8 w-56 font-mono text-xs', className)}
        aria-label="Switch active repo"
      >
        <SelectValue placeholder="Pick a repo…">
          {activeRepo ? `${activeRepo.owner}/${activeRepo.name}` : 'Pick a repo…'}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {repos.map((r) => (
          <SelectItem key={r.id} value={r.id} className="font-mono text-xs">
            {r.owner}/{r.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
